/**
 * The intake form's state, its client-side checks, and the request body.
 *
 * The checks mirror the api's `parseLead` (apps/studio/api/src/validation.ts)
 * so a visitor hears about a problem on the step where they can fix it, not
 * after pressing Send. The server stays the authority: anything it still
 * refuses comes back as field names and lands on the right step.
 */
import {
  type AgentAccess,
  type AgentChannel,
  type AgentTask,
  type BlogWriter,
  type Budget,
  type Category,
  type Channel,
  type EventType,
  type GuestCount,
  type HasDomain,
  type HeardFrom,
  type LogoAddon,
  type LogoNeed,
  type LogoStyle,
  type LogoUse,
  type OptimizeGoal,
  type PersonalType,
  type RsvpAddress,
  type RsvpFeature,
  type SiteAccess,
  type SitePlatform,
  type Timeline,
  type UserCount,
  type WebAppCurrent,
  type WebAppFeature,
  type WebAppUser,
  CONSENT_VERSION,
  LOGO_SCOPE_VERSION,
  budgetsFor,
  channelNeedsMobile,
  logoNeedsCurrent,
  logoNeedsScope,
  normalizeEmail,
  normalizePhone,
} from '@codasc/studio-shared';
import type { Attribution } from './track';

export interface FormState {
  category: Category | null;
  categoryOther: string;
  description: string;
  timeline: Timeline | null;
  eventDate: string;
  budget: Budget | null;
  references: string;
  hasDomain: HasDomain | null;
  webApp: {
    users: WebAppUser[];
    features: WebAppFeature[];
    current: WebAppCurrent | null;
    userCount: UserCount | null;
  };
  webOpt: {
    siteUrl: string;
    goals: OptimizeGoal[];
    platform: SitePlatform | null;
    access: SiteAccess | null;
  };
  agent: {
    channels: AgentChannel[];
    channelOther: string;
    tasks: AgentTask[];
    access: AgentAccess | null;
    connects: string;
  };
  rsvp: {
    eventType: EventType | null;
    guestCount: GuestCount | null;
    features: RsvpFeature[];
    address: RsvpAddress | null;
  };
  personal: {
    siteType: PersonalType | null;
    profession: string;
    migrateFrom: string;
    blogWriter: BlogWriter | null;
  };
  logo: {
    need: LogoNeed | null;
    styles: LogoStyle[];
    uses: LogoUse[];
    colors: string;
    currentLogoUrl: string;
    addons: LogoAddon[];
    /** The flat-vector scope box. */
    scope: boolean;
    /** The "my SVG is layered" box, for animating. */
    layeredSvg: boolean;
    /** The optional "show it in your portfolio" box. */
    portfolio: boolean;
  };
  name: string;
  channel: Channel | null;
  mobile: string;
  email: string;
  organization: string;
  heardFrom: HeardFrom | null;
  consent: boolean;
  /** Honeypot. Hidden; only a bot types here. */
  website: string;
}

export const EMPTY: FormState = {
  category: null,
  categoryOther: '',
  description: '',
  timeline: null,
  eventDate: '',
  budget: null,
  references: '',
  hasDomain: null,
  webApp: { users: [], features: [], current: null, userCount: null },
  webOpt: { siteUrl: '', goals: [], platform: null, access: null },
  agent: { channels: [], channelOther: '', tasks: [], access: null, connects: '' },
  rsvp: { eventType: null, guestCount: null, features: [], address: null },
  personal: { siteType: null, profession: '', migrateFrom: '', blogWriter: null },
  logo: {
    need: null,
    styles: [],
    uses: [],
    colors: '',
    currentLogoUrl: '',
    addons: [],
    scope: false,
    layeredSvg: false,
    portfolio: false,
  },
  name: '',
  channel: null,
  mobile: '',
  email: '',
  organization: '',
  heardFrom: null,
  consent: false,
  website: '',
};

/** field name → message, for the step being checked. */
export type Errors = Record<string, string>;

/**
 * Categories where "who do you work for" is a fair question. Business work
 * asks it; a personal site and an event do not. Read by both the form and the
 * body it sends, so they cannot diverge.
 */
export const ASKS_ORGANIZATION: readonly Category[] = ['web_app', 'web_opt', 'ai_agent', 'logo'];

/** Where the organization is not a nicety but the thing being drawn. */
export function needsOrganization(category: Category | null): boolean {
  return category === 'logo';
}

export const MIN_DESCRIPTION = 10;

function todayIso(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export function checkDetails(s: FormState): Errors {
  const e: Errors = {};
  if (s.category === 'custom' && !s.categoryOther.trim()) {
    e.categoryOther = 'Tell us what you have in mind.';
  }
  if (s.description.trim().length < MIN_DESCRIPTION) {
    e.description = 'A sentence or two is enough — what is it for, and who uses it?';
  }
  if (s.category === 'rsvp') {
    if (!s.eventDate) e.eventDate = 'When is the event?';
    else if (s.eventDate < todayIso()) e.eventDate = 'That date has passed.';
    if (!s.rsvp.eventType) e['details.eventType'] = 'Pick the kind of event.';
  } else if (!s.timeline) {
    e.timeline = 'Pick a timeline — "Flexible" is fine.';
  }
  if (!s.budget || (s.category && !budgetsFor(s.category).some((b) => b.id === s.budget))) {
    e.budget = 'Pick a range — "Not sure yet" is fine.';
  }
  if (s.category === 'personal' && !s.personal.siteType) {
    e['details.siteType'] = 'Pick the kind of site.';
  }
  if (s.category === 'web_opt') {
    if (!s.webOpt.siteUrl.trim()) e['details.siteUrl'] = 'Where can we see it?';
    if (s.webOpt.goals.length === 0) {
      e['details.goals'] = 'Pick at least one — "Not sure yet" is fine.';
    }
  }
  if (s.category === 'ai_agent') {
    if (s.agent.channels.length === 0) {
      e['details.channels'] = 'Where should people talk to it?';
    } else if (s.agent.channels.includes('other') && !s.agent.channelOther.trim()) {
      e['details.channelOther'] = 'Which app is it?';
    }
  }
  if (s.category === 'logo') {
    const need = s.logo.need;
    if (!need) {
      e['details.need'] = 'Pick what you need.';
    } else {
      if (logoNeedsCurrent(need) && !s.logo.currentLogoUrl.trim()) {
        e['details.currentLogoUrl'] = 'Where can we see your logo?';
      }
      if (logoNeedsScope(need) && !s.logo.scope) {
        e['details.scopeVersion'] = 'Please tick this — it’s the kind of logo we make.';
      }
      if (need === 'animate' && !s.logo.layeredSvg) {
        e['details.layeredSvg'] =
          'We need a layered SVG to animate it. Only have a JPG or PNG? Pick Redraw.';
      }
    }
  }
  return e;
}

export function checkContact(s: FormState): Errors {
  const e: Errors = {};
  if (!s.name.trim()) e.name = 'Your name, so we know who to ask for.';
  if (!s.channel) e.channel = 'How should we reach you?';
  const mobile = normalizePhone(s.mobile);
  const email = normalizeEmail(s.email);
  if (s.mobile.trim() && !mobile) e.mobile = 'That number doesn’t look right — e.g. 0917 123 4567.';
  if (s.email.trim() && !email) e.email = 'That email doesn’t look right.';
  if (s.channel && channelNeedsMobile(s.channel) && !mobile && !e.mobile) {
    e.mobile = 'We need your mobile number for this.';
  }
  if (s.channel === 'email' && !email && !e.email) e.email = 'We need your email for this.';
  if (!mobile && !email && !e.mobile && !e.email) {
    e.mobile = 'Leave a mobile number or an email, in case we can’t connect.';
  }
  if (needsOrganization(s.category) && !s.organization.trim()) {
    e.organization = 'Your business name, as it should appear on the logo.';
  }
  if (!s.consent) e.consent = 'Please tick this so we can contact you.';
  return e;
}

/** Which step owns a server-reported field, so a 400 lands where it's fixable. */
export function stepOfField(field: string): 2 | 3 {
  const contact = ['name', 'channel', 'mobile', 'email', 'organization', 'heardFrom', 'consent'];
  return contact.includes(field.split('.')[0]!) ? 3 : 2;
}

function details(s: FormState): Record<string, unknown> {
  switch (s.category) {
    case 'web_app':
      return {
        users: s.webApp.users,
        features: s.webApp.features,
        ...(s.webApp.current ? { current: s.webApp.current } : {}),
        ...(s.webApp.userCount ? { userCount: s.webApp.userCount } : {}),
      };
    case 'web_opt':
      return {
        siteUrl: s.webOpt.siteUrl.trim(),
        goals: s.webOpt.goals,
        ...(s.webOpt.platform ? { platform: s.webOpt.platform } : {}),
        ...(s.webOpt.access ? { access: s.webOpt.access } : {}),
      };
    case 'ai_agent':
      return {
        channels: s.agent.channels,
        tasks: s.agent.tasks,
        ...(s.agent.channels.includes('other') && s.agent.channelOther.trim()
          ? { channelOther: s.agent.channelOther }
          : {}),
        ...(s.agent.access ? { access: s.agent.access } : {}),
        ...(s.agent.connects.trim() ? { connects: s.agent.connects } : {}),
      };
    case 'rsvp':
      return {
        eventType: s.rsvp.eventType,
        features: s.rsvp.features,
        ...(s.rsvp.guestCount ? { guestCount: s.rsvp.guestCount } : {}),
        ...(s.rsvp.address ? { address: s.rsvp.address } : {}),
      };
    case 'personal':
      return {
        siteType: s.personal.siteType,
        ...(s.personal.profession.trim() ? { profession: s.personal.profession } : {}),
        ...(s.personal.migrateFrom.trim() ? { migrateFrom: s.personal.migrateFrom } : {}),
        ...(s.personal.siteType === 'blog' && s.personal.blogWriter
          ? { blogWriter: s.personal.blogWriter }
          : {}),
      };
    case 'logo': {
      const need = s.logo.need;
      return {
        need,
        styles: need === 'new' ? s.logo.styles : [],
        uses: s.logo.uses,
        ...(need !== 'animate' && s.logo.colors.trim() ? { colors: s.logo.colors } : {}),
        ...(need && logoNeedsCurrent(need) && s.logo.currentLogoUrl.trim()
          ? { currentLogoUrl: s.logo.currentLogoUrl.trim() }
          : {}),
        addons: s.logo.addons,
        ...(need && logoNeedsScope(need) && s.logo.scope
          ? { scopeVersion: LOGO_SCOPE_VERSION }
          : {}),
        ...(need === 'animate' && s.logo.layeredSvg ? { layeredSvg: true } : {}),
        ...(s.logo.portfolio ? { portfolio: true } : {}),
      };
    }
    default:
      return {};
  }
}

export function toBody(
  s: FormState,
  attribution: Attribution,
  meta: { eventId: string; fbc?: string; fbp?: string; externalId?: string },
) {
  const refs = s.references
    .split(/[\s,]+/)
    .map((r) => r.trim())
    .filter(Boolean)
    .slice(0, 5);
  return {
    category: s.category,
    ...(s.category === 'custom' ? { categoryOther: s.categoryOther } : {}),
    description: s.description,
    ...(s.category === 'rsvp' ? { eventDate: s.eventDate } : { timeline: s.timeline }),
    budget: s.budget,
    references: refs,
    ...(s.hasDomain ? { hasDomain: s.hasDomain } : {}),
    details: details(s),
    name: s.name,
    channel: s.channel,
    ...(s.mobile.trim() ? { mobile: s.mobile } : {}),
    ...(s.email.trim() ? { email: s.email } : {}),
    ...(ASKS_ORGANIZATION.includes(s.category as Category) && s.organization.trim()
      ? { organization: s.organization }
      : {}),
    ...(s.heardFrom ? { heardFrom: s.heardFrom } : {}),
    consent: s.consent,
    consentVersion: CONSENT_VERSION,
    website: s.website,
    attribution,
    meta,
  };
}
